import { Component } from "react";
import { ColumnConfig, SortDirections, numberSort, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { ServicingProviderCrossCountyResult } from "@app/projects/results/servicing-provider/servicingProviderDetail";

interface CrossCountyResultsProps {
  records: ServicingProviderCrossCountyResult[];
  showGainsAndImpact: boolean;
}

interface CrossCountyResultsState {
  sortedRecords: CrossCountyTableData[];
}

interface CrossCountyTableData {
  [wafflesKeyColumnName]: string;
  state: string;
  county: string;
  regionSetStatusDescription: string;
  servesPercent: string;
  gainsPercent?: string;
  impactsPercent?: string;
}

export default class CrossCountyResults extends Component<CrossCountyResultsProps, CrossCountyResultsState> {
  static contextType = FeatureFlagContext;
  declare context: React.ContextType<typeof FeatureFlagContext>;

  constructor(props: CrossCountyResultsProps) {
    super(props);

    this.state = {
      sortedRecords: this.buildRecords(),
    };
  }

  public componentDidUpdate(prevProps: CrossCountyResultsProps) {
    if (prevProps.records !== this.props.records) {
      this.setState({
        sortedRecords: this.buildRecords(),
      });
    }
  }

  public render() {
    if (!this.state.sortedRecords || this.state.sortedRecords.length === 0) {
      return (<>No cross-county results found.</>);
    }

    return <Waffles<CrossCountyTableData>
      columns={this.getColumnConfig()}
      customSort={this.defaultSort}
      data={this.state.sortedRecords}
      testId="cross-county-results"
    />
  }

  private buildRecords = () => {
    /* oxlint-disable sort-keys */
    return this.props.records.map((record, index) => ({
      key: index.toString(),
      state: record.state,
      county: record.county,
      countyFIPS: record.countyFIPS,
      regionSetStatusDescription: record.regionSetStatusDescription,
      servesPercent: `${record.servesPercent}%`,
      gainsPercent: `${record.gainsPercent}%`,
      impactsPercent: `${record.impactsPercent}%`,
    }));
    /* oxlint-enable */
  }

  private defaultSort = (records: CrossCountyTableData[], columnKey: keyof CrossCountyTableData, sortDirection: SortDirections) => {
    if (columnKey === "state" || sortDirection === SortDirections.None) {
      if (sortDirection === SortDirections.Desc) {
        records.sort((a, b) => {
          if (a.state === b.state) {
            if (a.county < b.county) {
              return 1;
            } else {
              return -1;
            }
          } else if (a.state < b.state) {
            return 1;
          } else {
            return -1;
          }
        });
      } else {
        records.sort((a, b) => {
          if (a.state === b.state) {
            if (a.county > b.county) {
              return 1;
            } else {
              return -1;
            }
          } else if (a.state > b.state) {
            return 1;
          } else {
            return -1;
          }
        });
      }

      return records;
    } else {
      if (columnKey === "servesPercent" || columnKey === "gainsPercent" || columnKey === "impactsPercent") {
        return numberSort(records, columnKey, sortDirection === SortDirections.Asc ? 1 : -1);
      }
      return records.sort((a, b) => {
        return (a[columnKey] > b[columnKey] ? 1 : -1) * (sortDirection === SortDirections.Asc ? 1 : -1);
      })
    }
  }

  private getColumnConfig(): ColumnConfig {
    return {
      county: {
        displayText: "County",
        testId: "county",
      },
      countyFIPS: {
        displayText: "County FIPS",
        hidden: !this.context.enabledFeatureFlags.has(Features.SSA_TO_FIPS),
        testId: "county-fips",
      },
      gainsPercent: {
        displayText: "Gains",
        hidden: !this.props.showGainsAndImpact,
        testId: "gains-percent"
      },
      impactsPercent: {
        displayText: "Impacts",
        hidden: !this.props.showGainsAndImpact,
        testId: "impacts-percent"
      },
      regionSetStatusDescription: {
        displayText: "Status",
        testId: "region-set-status-description",
      },
      servesPercent: {
        displayText: "Serves",
        testId: "serves-percent"
      },
      state: {
        displayText: "State",
        testId: "state",
      },
    }
  }
}
